/**
 * @file Applies a column's `CellCleaningRule[]` in array order to one raw cell
 * value, delegating numeric coercion to the shared `coerceCellNumber` in
 * `#types` — the SAME functions the cloud_run apply writers run, so the Mapping
 * Review before/after previews match exactly what lands. `coerceNumber` keeps the
 * original value when it isn't a clean number and `coerceDate` serializes a parsed
 * date to a canonical ISO string (both keep the raw on failure — cleaning never
 * destroys data); an optional `locale`/`dateLocale` fixes the separator/date order,
 * and `centuryPivot`/`dateZone` retarget the two-digit-year window and the zone-less
 * wall clock, all per the staged doc. Kept byte-for-byte parallel to the cloud_run
 * writer `applyColumnRules`.
 */
import type { CellCleaningRule, DateLocale, NumberLocale } from '#types';
import { coerceCellDate, coerceCellNumber } from '#types';

export function applyCellCleaningRules(
  value: string | number | null,
  rules: readonly CellCleaningRule[],
  locale?: NumberLocale,
  dateLocale?: DateLocale,
  centuryPivot?: number,
  dateZone?: string,
): string | number | null {
  return rules.reduce<string | number | null>((v, rule) => {
    if (v == null) return null;
    if (rule.kind === 'trim') return typeof v === 'string' ? v.trim() : v;
    if (rule.kind === 'lowercase') return typeof v === 'string' ? v.toLowerCase() : v;
    if (rule.kind === 'uppercase') return typeof v === 'string' ? v.toUpperCase() : v;
    if (rule.kind === 'replace') {
      return typeof v === 'string' ? v.split(rule.find).join(rule.replaceWith) : v;
    }
    if (rule.kind === 'coerceNumber') return coerceCellNumber(v, locale) ?? v;
    if (rule.kind === 'coerceDate') return coerceCellDate(v, rule.dateType, dateLocale, centuryPivot, dateZone)?.toISOString() ?? v;
    return v;
  }, value);
}
